import {Connection} from '@apideck/vault-js';
import {Department, HRIS, IntegrationPlatform} from 'hubbl-shared';
import {Api} from '../../api';
import {getAuthUserCompanyId} from '../../utils';
import {createAppAsyncThunk} from '../appAsyncThunk';
import {UserSelector} from '../user/UserSelector';
import {CompanySelector} from './CompanySelector';
import {isValidSlackWebhookUrl} from '../../utils/slack/isValidSlackWebhookUrl';
import {isValidTeamsWebhookUrl} from '../../utils/teams/isValidTeamsWebhookUrl';

export const addHRIStoCompany = createAppAsyncThunk<HRIS, Connection>(
  'company/addHRIStoCompany',
  async (hrisConnection, {getState}) => {
    if (!hrisConnection.enabled || hrisConnection.state !== 'callable') {
      throw Error('error - connection not enabled or not callable. No succes');
    }
    const userDetails = UserSelector.getUserDetails(getState());

    if (!userDetails) {
      throw Error('error - no user details found');
    }

    try {
      const userCompanyId = await getAuthUserCompanyId();

      const newHRIS: HRIS = {
        createdAtMilliseconds:
          hrisConnection.created_at ?? new Date().getMilliseconds(),
        icon: hrisConnection.icon ?? '',
        logo: hrisConnection.logo ?? '',
        id: hrisConnection.id,
        integratedByUserId: userDetails.id,
        name: hrisConnection.name,
        website: hrisConnection.website ?? '',
      };

      await Api.company.addHRIStoCompany(userCompanyId, newHRIS);

      return newHRIS;
    } catch (error) {
      console.log(error);
      throw Error('error adding the HRIS to company');
    }
  },
);

export const fetchCompany = createAppAsyncThunk(
  'company/fetchCompany',
  async () => {
    const userCompanyId = await getAuthUserCompanyId();

    let companyDetails =
      await Api.company.getLoggedInUserCompany(userCompanyId);

    // Get general settings and combine with company suggestions
    const generalSettings = await Api.generalSettings.getDefaultConfig();
    let hubblGroupSuggestions: any[] = [];

    if (generalSettings && companyDetails) {
      hubblGroupSuggestions = [
        ...(companyDetails.hubblGroupSuggestions || []),
        ...(generalSettings.hubblGroupSuggestions || []),
      ];

      companyDetails = {
        ...companyDetails,
        hubblGroupSuggestions,
      };
    }

    return companyDetails;
  },
);

// Fetch events thunk with pagination support
export const fetchCompanyEmployees = createAppAsyncThunk(
  'user/fetchCompanyEmployees',
  async (_, {getState}) => {
    try {
      const LIMIT_COUNT = 999;
      const {lastVisible} = CompanySelector.getCompanyEmployees(getState());
      const userCompanyId = await getAuthUserCompanyId();

      const {employees, lastVisibleSnapshot} =
        await Api.company.getCompanyEmployees(
          userCompanyId,
          LIMIT_COUNT,
          lastVisible,
        );

      //Filter out hubbl admins
      const filteredEmployees = employees.filter(
        employee => !employee.email.includes('@hubbl.co'),
      );

      // Return payload with events and lastVisible document snapshot
      return {
        employees: filteredEmployees,
        lastVisible: lastVisibleSnapshot, // Make sure we pass the actual snapshot
        isNextPage: !!lastVisible, // Flag for pagination - true if this is not the first page
      };
    } catch (error) {
      console.error('Error fetching company employees:', error);
      throw new Error('Error fetching company employees');
    }
  },
);

// Fetch all company employees at once (no pagination)
export const fetchAllCompanyEmployees = createAppAsyncThunk(
  'user/fetchAllCompanyEmployees',
  async () => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      const employees = await Api.company.getAllCompanyEmployees(userCompanyId);

      // Filter out hubbl admins
      const filteredEmployees = employees.filter(
        employee => !employee.email.includes('@hubbl.co'),
      );

      return {
        employees: filteredEmployees,
      };
    } catch (error) {
      console.error('Error fetching all company employees:', error);
      throw new Error('Error fetching all company employees');
    }
  },
);

export const updateSubCompanySlackChannel = createAppAsyncThunk<
  void,
  {
    locationId: string;
    subCompanyId: string;
    slackWebhookUrl: string;
  }
>(
  'company/updateSubCompanySlackChannel',
  async ({locationId, subCompanyId, slackWebhookUrl}, {dispatch, getState}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      // Validate Slack webhook URL format
      if (slackWebhookUrl && !isValidSlackWebhookUrl(slackWebhookUrl)) {
        throw new Error('Invalid Slack webhook URL format');
      }

      await Api.company.updateSubCompanySlackChannel(
        userCompanyId,
        locationId,
        subCompanyId,
        slackWebhookUrl,
      );

      // Refetch company data to get updated organizational metadata
      await dispatch(fetchCompany()).unwrap();
      return;
    } catch (error) {
      console.error('Error updating sub-company Slack channel:', error);
      throw new Error('Error updating sub-company Slack channel');
    }
  },
);

export const updateGeneralSlackChannel = createAppAsyncThunk<
  void,
  {
    webhookUrl: string;
  }
>(
  'company/updateGeneralSlackChannel',
  async ({webhookUrl}, {dispatch, getState}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      // Validate Slack webhook URL format
      if (webhookUrl && !isValidSlackWebhookUrl(webhookUrl)) {
        throw new Error('Invalid Slack webhook URL format');
      }

      await Api.company.updateGeneralSlackChannel(userCompanyId, webhookUrl);

      // Refetch company data to get updated settings
      await dispatch(fetchCompany()).unwrap();
      return;
    } catch (error) {
      console.error('Error updating general Slack channel:', error);
      throw new Error('Error updating general Slack channel');
    }
  },
);

export const updateSubCompanyTeamsChannel = createAppAsyncThunk<
  void,
  {
    locationId: string;
    subCompanyId: string;
    teamsWebhookUrl: string;
  }
>(
  'company/updateSubCompanyTeamsChannel',
  async ({locationId, subCompanyId, teamsWebhookUrl}, {dispatch, getState}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      // Validate Teams webhook URL format
      if (teamsWebhookUrl && !isValidTeamsWebhookUrl(teamsWebhookUrl)) {
        throw new Error('Invalid Teams webhook URL format');
      }

      await Api.company.updateSubCompanyTeamsChannel(
        userCompanyId,
        locationId,
        subCompanyId,
        teamsWebhookUrl,
      );

      // Refetch company data to get updated organizational metadata
      await dispatch(fetchCompany()).unwrap();
      return;
    } catch (error) {
      console.error('Error updating sub-company Teams channel:', error);
      throw new Error('Error updating sub-company Teams channel');
    }
  },
);

export const updateGeneralTeamsChannel = createAppAsyncThunk<
  void,
  {
    webhookUrl: string;
  }
>(
  'company/updateGeneralTeamsChannel',
  async ({webhookUrl}, {dispatch, getState}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      // Validate Teams webhook URL format
      if (webhookUrl && !isValidTeamsWebhookUrl(webhookUrl)) {
        throw new Error('Invalid Teams webhook URL format');
      }

      await Api.company.updateGeneralTeamsChannel(userCompanyId, webhookUrl);

      // Refetch company data to get updated settings
      await dispatch(fetchCompany()).unwrap();
      return;
    } catch (error) {
      console.error('Error updating general Teams channel:', error);
      throw new Error('Error updating general Teams channel');
    }
  },
);

export const updateIntegrationPlatform = createAppAsyncThunk<
  void,
  {
    platform: IntegrationPlatform;
  }
>(
  'company/updateIntegrationPlatform',
  async ({platform}, {dispatch, getState}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      await Api.company.updateIntegrationPlatform(userCompanyId, platform);

      // Refetch company data to get updated settings
      await dispatch(fetchCompany()).unwrap();
      return;
    } catch (error) {
      console.error('Error updating integration platform:', error);
      throw new Error('Error updating integration platform');
    }
  },
);

export const deleteIntegration = createAppAsyncThunk<void, void>(
  'company/deleteIntegration',
  async (_, {dispatch}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      await Api.company.deleteIntegration(userCompanyId);

      // Refetch company data to get cleared settings
      await dispatch(fetchCompany()).unwrap();
      return;
    } catch (error) {
      console.error('Error deleting integration:', error);
      throw new Error('Error deleting integration');
    }
  },
);

export const addCompanyDepartment = createAppAsyncThunk<Department, Department>(
  'company/addCompanyDepartment',
  async (newDepartment, {dispatch}) => {
    try {
      const userCompanyId = await getAuthUserCompanyId();

      await Api.company.addCompanyDepartment(userCompanyId, newDepartment);

      // Refetch company data to get updated departments list
      await dispatch(fetchCompany()).unwrap();
      return newDepartment;
    } catch (error) {
      console.error('Error adding company department:', error);
      throw new Error('Error adding company department');
    }
  },
);
